import * as THREE from 'three';
import { FikoDinamikKesit, FikoKesitManipulator } from './FikoDinamikKesit';

/**
 * Fiko Dinamik Kesit - Mouse Interaction Handler
 *
 * CATIA tarzı drag-and-drop manipülatör kontrolü
 */
export class FikoDinamikKesitInteraction {
    private raycaster: THREE.Raycaster;
    private mouse: THREE.Vector2;
    private isDragging: boolean = false;
    private dragMode: 'translate' | 'rotate' | 'axis' | 'boundary' | null = null;
    private selectedManipulator: FikoKesitManipulator | null = null;
    private dragStartPoint: THREE.Vector3 | null = null;
    private dragPlane: THREE.Plane | null = null;
    private initialPlaneConstant: number = 0;
    private initialPlaneNormal: THREE.Vector3 | null = null;
    private dragAxisIndex: number | null = null; // 0=X, 1=Y, 2=Z (for axis dragging)

    // Hover state
    private hoveredObject: THREE.Object3D | null = null;
    private originalMaterial: THREE.Material | THREE.Material[] | null = null;

    // Event listeners
    private boundMouseDown: (event: MouseEvent) => void;
    private boundMouseMove: (event: MouseEvent) => void;
    private boundMouseUp: (event: MouseEvent) => void;

    constructor(
        private dinamikKesit: FikoDinamikKesit,
        private camera: THREE.Camera,
        private canvas: HTMLElement
    ) {
        this.raycaster = new THREE.Raycaster();
        this.raycaster.params.Line = { threshold: 5 }; // Çizgilere daha kolay tıklama
        this.mouse = new THREE.Vector2();

        // Bind event listeners
        this.boundMouseDown = this.onMouseDown.bind(this);
        this.boundMouseMove = this.onMouseMove.bind(this);
        this.boundMouseUp = this.onMouseUp.bind(this);
    }

    /**
     * Interaktif kontrolü başlat
     */
    public baslat(): void {
        this.canvas.addEventListener('mousedown', this.boundMouseDown);
        this.canvas.addEventListener('mousemove', this.boundMouseMove);
        this.canvas.addEventListener('mouseup', this.boundMouseUp);
        this.canvas.style.cursor = 'default';
    }

    /**
     * Interaktif kontrolü durdur
     */
    public durdur(): void {
        this.canvas.removeEventListener('mousedown', this.boundMouseDown);
        this.canvas.removeEventListener('mousemove', this.boundMouseMove);
        this.canvas.removeEventListener('mouseup', this.boundMouseUp);
        this.canvas.style.cursor = 'default';
        this.clearHover();
    }

    /**
     * Mouse down event
     */
    private onMouseDown(event: MouseEvent): void {
        this.updateMousePosition(event);

        // Raycast manipülatörlere
        const intersects = this.raycastManipulators();

        if (intersects.length > 0) {
            const intersect = intersects[0];
            let manipulator = this.findManipulatorFromObject(intersect.object);

            // Eğer boundary (kesit çizgisi) seçildiyse
            if (!manipulator && this.isBoundary(intersect.object)) {
                // Kesit çizgisinden plane'i bul
                if (intersect.object.userData?.isSectionLine && intersect.object.userData?.plane) {
                    const sectionPlane = intersect.object.userData.plane as THREE.Plane;
                    // Bu plane'e ait manipulator'u bul
                    const planeIndex = this.dinamikKesit.kesitDuzlemleri.indexOf(sectionPlane);
                    if (planeIndex >= 0) {
                        manipulator = this.dinamikKesit.manipulatorler[planeIndex];
                        // Bu düzlemi aktif yap
                        this.dinamikKesit.aktifDuzlemDegistir(planeIndex);
                    }
                }
                // Hala bulunamadıysa aktif manipulator'u kullan
                if (!manipulator) {
                    manipulator = this.dinamikKesit.manipulatorler[this.dinamikKesit.aktifDuzlemIndex];
                }
            }

            if (manipulator) {
                this.selectedManipulator = manipulator;
                this.dragStartPoint = intersect.point.clone();

                // Drag mode belirle
                if (this.isAxis(intersect.object)) {
                    // Eksen sürükleme - Plane normal'e dik iki ekseni sürükle
                    this.dragMode = 'axis';
                    this.dragAxisIndex = intersect.object.userData?.axisIndex ?? null;

                    const axisName = ['X', 'Y', 'Z'][this.dragAxisIndex ?? 0];

                    // Eksen yönünü belirle
                    const axisDir = new THREE.Vector3();
                    if (this.dragAxisIndex === 0) {
                        axisDir.set(1, 0, 0); // X
                    } else if (this.dragAxisIndex === 1) {
                        axisDir.set(0, 1, 0); // Y
                    } else if (this.dragAxisIndex === 2) {
                        axisDir.set(0, 0, 1); // Z
                    }

                    // Drag plane: eksen yönüne perpendicular
                    const camDir = new THREE.Vector3();
                    this.camera.getWorldDirection(camDir);
                    let planeNormal = new THREE.Vector3().crossVectors(axisDir, camDir);

                    if (planeNormal.length() < 0.1) {
                        const refDir = Math.abs(axisDir.y) < 0.9 ?
                            new THREE.Vector3(0, 1, 0) :
                            new THREE.Vector3(1, 0, 0);
                        planeNormal = new THREE.Vector3().crossVectors(axisDir, refDir);
                    }
                    planeNormal.normalize();

                    this.dragPlane = new THREE.Plane(planeNormal, 0);
                    this.dragPlane.setFromNormalAndCoplanarPoint(planeNormal, intersect.point);

                    this.initialPlaneNormal = axisDir.clone();
                    this.initialPlaneConstant = manipulator.plane.constant;

                } else if (this.isBoundary(intersect.object)) {
                    // Kesit sınırlarından sürükle
                    this.dragMode = 'boundary';

                    // Plane normal yönünde sürükle
                    const camDir = new THREE.Vector3();
                    this.camera.getWorldDirection(camDir);
                    this.dragPlane = new THREE.Plane();
                    this.dragPlane.setFromNormalAndCoplanarPoint(camDir.negate(), intersect.point);

                    this.initialPlaneConstant = manipulator.plane.constant;
                    this.initialPlaneNormal = manipulator.plane.normal.clone();

                } else if (this.isArrow(intersect.object) || this.isPlaneMesh(intersect.object)) {
                    this.dragMode = 'translate';

                    // Kamera düzlemine paralel drag plane oluştur (daha kolay kontrol için)
                    const camDir = new THREE.Vector3();
                    this.camera.getWorldDirection(camDir);
                    this.dragPlane = new THREE.Plane();
                    this.dragPlane.setFromNormalAndCoplanarPoint(camDir.negate(), intersect.point);
                    this.initialPlaneConstant = manipulator.plane.constant;
                    this.initialPlaneNormal = manipulator.plane.normal.clone();
                } else if (this.isRotationRing(intersect.object)) {
                    this.dragMode = 'rotate';
                    this.initialPlaneNormal = manipulator.plane.normal.clone();

                    // Kamera düzlemine paralel drag plane
                    const camDir = new THREE.Vector3();
                    this.camera.getWorldDirection(camDir);
                    this.dragPlane = new THREE.Plane(camDir.negate(), 0);
                    this.dragPlane.setFromNormalAndCoplanarPoint(
                        camDir,
                        intersect.point
                    );
                }

                this.isDragging = true;

                // Cursor stil ayarla
                if (this.dragMode === 'axis' || this.dragMode === 'boundary') {
                    this.canvas.style.cursor = 'move';
                } else if (this.dragMode === 'translate') {
                    this.canvas.style.cursor = 'move';
                } else if (this.dragMode === 'rotate') {
                    this.canvas.style.cursor = 'grab';
                }

                // Diğer sistemleri devre dışı bırak
                event.stopPropagation();
                event.preventDefault();
            }
        }
    }

    /**
     * Mouse move event
     */
    private onMouseMove(event: MouseEvent): void {
        this.updateMousePosition(event);

        if (this.isDragging && this.selectedManipulator && this.dragPlane && this.dragStartPoint) {
            // Drag işlemi
            this.raycaster.setFromCamera(this.mouse, this.camera);
            const intersectPoint = new THREE.Vector3();
            this.raycaster.ray.intersectPlane(this.dragPlane, intersectPoint);

            if (intersectPoint) {
                if (this.dragMode === 'axis' && this.initialPlaneNormal) {
                    // Eksen sürükleme: Sadece seçili eksen yönünde hareket
                    const movement = intersectPoint.clone().sub(this.dragStartPoint);

                    // Hareket vektörünü eksen yönüne project et
                    const axisMovement = movement.dot(this.initialPlaneNormal);

                    // Plane constant'ı güncelle (eksen yönüne göre)
                    this.selectedManipulator.plane.constant = this.initialPlaneConstant - axisMovement;

                    // Manipülatörü güncelle
                    this.selectedManipulator.guncelle();

                    // Clipping planes'leri güncelle
                    this.dinamikKesit['clippingPlanleriGuncelle']();

                } else if (this.dragMode === 'boundary' && this.initialPlaneNormal) {
                    // Kesit boundary sürükleme: Plane normal yönünde hareket
                    const movement = intersectPoint.clone().sub(this.dragStartPoint);

                    // Hareket vektörünü plane normal'ine project et
                    const normalMovement = movement.dot(this.initialPlaneNormal);

                    // Plane constant'ı güncelle
                    this.selectedManipulator.plane.constant = this.initialPlaneConstant - normalMovement;

                    // Manipülatörü güncelle
                    this.selectedManipulator.guncelle();

                    // Clipping planes'leri güncelle
                    this.dinamikKesit['clippingPlanleriGuncelle']();

                } else if (this.dragMode === 'translate' && this.initialPlaneNormal) {
                    // Translation: Mouse hareketini düzlem normal'i yönüne project et
                    const movement = intersectPoint.clone().sub(this.dragStartPoint);

                    // Hareket vektörünü düzlem normal'ine project et
                    const normalMovement = movement.dot(this.initialPlaneNormal);

                    // Plane constant'ı güncelle (işaret ters çünkü THREE.js Plane matematigi)
                    this.selectedManipulator.plane.constant = this.initialPlaneConstant - normalMovement;

                    // Manipülatörü güncelle
                    this.selectedManipulator.guncelle();

                    // Clipping planes'leri güncelle
                    this.dinamikKesit['clippingPlanleriGuncelle']();

                } else if (this.dragMode === 'rotate' && this.initialPlaneNormal) {
                    // Rotation: Drag yönüne göre normal'i döndür
                    const movement = intersectPoint.clone().sub(this.dragStartPoint);
                    const rotationAxis = movement.clone().cross(this.camera.getWorldDirection(new THREE.Vector3())).normalize();
                    const rotationAngle = movement.length() * 0.01; // Sensitivity

                    const newNormal = this.initialPlaneNormal.clone();
                    newNormal.applyAxisAngle(rotationAxis, rotationAngle);
                    newNormal.normalize();

                    this.selectedManipulator.plane.normal.copy(newNormal);
                    this.selectedManipulator.guncelle();
                    this.dinamikKesit['clippingPlanleriGuncelle']();
                }

                // Sahneyi render et
                if (this.dinamikKesit.sahne?.anaGosterim) {
                    this.dinamikKesit.sahne.anaGosterim.ciz();
                }
            }

            event.stopPropagation();
            event.preventDefault();
        } else {
            // Hover effect
            const intersects = this.raycastManipulators();

            if (intersects.length > 0) {
                const intersect = intersects[0];
                this.setHover(intersect.object);

                if (this.isAxis(intersect.object)) {
                    this.canvas.style.cursor = 'move';
                } else if (this.isBoundary(intersect.object)) {
                    this.canvas.style.cursor = 'move';
                } else if (this.isArrow(intersect.object) || this.isPlaneMesh(intersect.object)) {
                    this.canvas.style.cursor = 'move';
                } else if (this.isRotationRing(intersect.object)) {
                    this.canvas.style.cursor = 'grab';
                } else {
                    this.canvas.style.cursor = 'pointer';
                }
            } else {
                this.clearHover();
                this.canvas.style.cursor = 'default';
            }
        }
    }

    /**
     * Mouse up event
     */
    private onMouseUp(event: MouseEvent): void {
        if (this.isDragging) {
            this.isDragging = false;
            this.dragMode = null;
            this.selectedManipulator = null;
            this.dragStartPoint = null;
            this.dragPlane = null;
            this.initialPlaneConstant = 0;
            this.initialPlaneNormal = null;
            this.dragAxisIndex = null;

            this.canvas.style.cursor = 'default';

            event.stopPropagation();
            event.preventDefault();
        }
    }

    /**
     * Mouse pozisyonunu güncelle
     */
    private updateMousePosition(event: MouseEvent): void {
        const rect = this.canvas.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    }

    /**
     * Manipülatörlere raycast
     */
    private raycastManipulators(): THREE.Intersection[] {
        this.raycaster.setFromCamera(this.mouse, this.camera);

        const objects: THREE.Object3D[] = [];

        // Manipulators üzerindeki meshes ve lines
        this.dinamikKesit.manipulatorler.forEach(manipulator => {
            manipulator.traverse(child => {
                if ((child as THREE.Mesh).isMesh && child.visible) {
                    objects.push(child);
                }
            });
        });

        // Kesit çizgileri (boundaries) - LineSegments/Lines
        if (this.dinamikKesit.kesitGeometrileri) {
            this.dinamikKesit.kesitGeometrileri.traverse(child => {
                if ((child instanceof THREE.LineSegments || child instanceof THREE.Line) && child.visible) {
                    objects.push(child);
                }
            });
        }

        return this.raycaster.intersectObjects(objects, false);
    }

    /**
     * Object'ten manipülatör bul
     */
    private findManipulatorFromObject(object: THREE.Object3D): FikoKesitManipulator | null {
        let current = object;
        while (current) {
            const manipulator = this.dinamikKesit.manipulatorler.find(m => m === current);
            if (manipulator) {
                return manipulator;
            }
            current = current.parent as THREE.Object3D;
        }
        return null;
    }

    /**
     * Object ok mu kontrol et
     */
    private isArrow(object: THREE.Object3D): boolean {
        // userData kontrolü ile daha güvenilir tespit
        return object.userData?.isArrow === true ||
               object.parent?.name === 'Translation Arrows' ||
               object.name.includes('arrow') ||
               object.name.includes('sap');
    }

    /**
     * Object rotation ring mi kontrol et
     */
    private isRotationRing(object: THREE.Object3D): boolean {
        // userData kontrolü ile daha güvenilir tespit
        return object.userData?.isRotationRing === true ||
               object.parent?.name === 'Rotation Rings' ||
               object.name.includes('ring');
    }

    /**
     * Object düzlem mesh mi kontrol et
     */
    private isPlaneMesh(object: THREE.Object3D): boolean {
        // Düzlem mesh veya grid helper
        return object.userData?.isPlaneMesh === true ||
               object.type === 'GridHelper' ||
               (object.parent && object.parent.userData?.isPlaneMesh === true);
    }

    /**
     * Object eksen mi kontrol et
     */
    private isAxis(object: THREE.Object3D): boolean {
        // AxesHelper veya eksen çizgileri
        return object.type === 'AxesHelper' ||
               object.name === 'axes_helper' ||
               object.name.includes('axis_line') ||
               (object.parent && object.parent.name === 'axes_helper');
    }

    /**
     * Object kesit sınırı (boundary) mi kontrol et
     */
    private isBoundary(object: THREE.Object3D): boolean {
        // Kesit çizgileri (LineSegments veya Line)
        if (object instanceof THREE.LineSegments || object instanceof THREE.Line) {
            return true;
        }

        // Kesit Geometrileri grubu içindeyse
        let current = object.parent;
        while (current) {
            if (current.name === 'Kesit Geometrileri') {
                return true;
            }
            current = current.parent;
        }

        return false;
    }

    /**
     * Hover effect ayarla
     */
    private setHover(object: THREE.Object3D): void {
        if (this.hoveredObject === object) {
            return;
        }

        this.clearHover();

        this.hoveredObject = object;
        const mesh = object as THREE.Mesh;

        // GridHelper için özel kontrol (material yapısı farklı)
        if (object.type === 'GridHelper') {
            // Grid helper için hover effect yapma (zaten düzlem mesh'e yapılıyor)
            return;
        }

        if (mesh.material) {
            this.originalMaterial = mesh.material;

            // Highlight material
            const highlightMaterial = Array.isArray(mesh.material)
                ? mesh.material.map(m => this.createHighlightMaterial(m))
                : this.createHighlightMaterial(mesh.material);

            mesh.material = highlightMaterial;

            // Render
            if (this.dinamikKesit.sahne?.anaGosterim) {
                this.dinamikKesit.sahne.anaGosterim.ciz();
            }
        }
    }

    /**
     * Hover effect temizle
     */
    private clearHover(): void {
        if (this.hoveredObject && this.originalMaterial) {
            const mesh = this.hoveredObject as THREE.Mesh;
            mesh.material = this.originalMaterial;

            this.hoveredObject = null;
            this.originalMaterial = null;

            // Render
            if (this.dinamikKesit.sahne?.anaGosterim) {
                this.dinamikKesit.sahne.anaGosterim.ciz();
            }
        }
    }

    /**
     * Highlight material oluştur
     */
    private createHighlightMaterial(material: THREE.Material): THREE.Material {
        const highlightMat = material.clone();

        if (highlightMat instanceof THREE.MeshBasicMaterial) {
            highlightMat.color.multiplyScalar(1.5); // Brighten
            highlightMat.opacity = Math.min(1, highlightMat.opacity * 1.2);
        }

        return highlightMat;
    }

    /**
     * Cleanup
     */
    public dispose(): void {
        this.durdur();
        this.clearHover();
    }
}
